import type { DataModel, DataModelAttribute, DataModelLayout, LayoutSection } from '@/types/DataModel';

const DAM_DETAIL_LAYOUT = 'asset_detail';

const DAM_DETAIL_SLOTS = ['HEADER_BADGES', 'ASSET_DATA'] as const;

type DamDetailSlot = typeof DAM_DETAIL_SLOTS[number];

/** A further section of `asset_detail`, shown as a collapsible group below `ASSET_DATA`. */
interface DamDetailGroup {
    name: string;
    label: string | null;
    attributes: DataModelAttribute[];
}

type DamDetailSections = Record<DamDetailSlot, DataModelAttribute[]> & { groups: DamDetailGroup[] };

/** System values end in `__` and are never edited on the detail page. */
const isDamSystemAttribute = (name: string) => name.endsWith('__');

const isSlot = (name: string): name is DamDetailSlot => (DAM_DETAIL_SLOTS as readonly string[]).includes(name);

/**
 * Attributes per detail area, taken from the sections of `asset_detail` by name and merged
 * with their data-model definition, so type, options and label are available. Every other
 * section becomes a group, in layout order (D-056).
 */
const resolveDamDetailSections = (layout: DataModelLayout | undefined, dataModel: DataModel | undefined): DamDetailSections => {
    const sections: DamDetailSections = { HEADER_BADGES: [], ASSET_DATA: [], groups: [] };

    const attributesOf = (section: LayoutSection) => (section.attributes ?? [])
        .filter((attribute) => !attribute.hidden)
        .map((attribute): DataModelAttribute => {
            const registered = dataModel?.attribute(attribute.name);
            return registered ? { ...registered, ...attribute } : attribute;
        });

    for (const section of layout?.sections ?? []) {
        if (isSlot(section.name)) {
            sections[section.name] = attributesOf(section);
        } else {
            const attributes = attributesOf(section);
            if (attributes.length > 0) {
                sections.groups.push({ name: section.name, label: section.label ?? null, attributes });
            }
        }
    }

    return sections;
};

export { DAM_DETAIL_LAYOUT, isDamSystemAttribute, resolveDamDetailSections };
export type { DamDetailGroup, DamDetailSections, DamDetailSlot };
